import { District, DistrictSource } from '../../app/feathers/districts/District';
import { DistrictVotingType } from '../../app/feathers/districts/DistrictVotingType';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import { NamingPattern } from '../../app/feathers/naming-patterns/NamingPattern';
import { prettyDistrictNameFromPattern } from '../../app/util/pretty-district-name-from-pattern';
import {
  DistrictIdentifierType,
  identifierPayload,
  identifierValueForDistrict,
} from '../Tier3EditDistrictModal/district-identifiers';

export interface CreateDistrictSuggestion {
  number?: number;
  name?: string;
  longName?: string;
  letter?: string;
  type?: string;
  parentId?: string;
  parentDistrict?: string;
}

export interface DistrictRow {
  identifier: District['name' | 'number' | 'letter'];
  originalIdentifier: District['name' | 'number' | 'letter'];
  longName?: string;
  votingType: DistrictVotingType;
  existingDistrictId?: string;
}

export const identifierTypeForSuggestion = (
  suggestion: CreateDistrictSuggestion,
): DistrictIdentifierType =>
  identifierTypeForValue(identifierValueForSuggestion(suggestion));

const identifierValueForSuggestion = (
  suggestion: CreateDistrictSuggestion,
): string | number =>
  suggestion.number ?? suggestion.letter ?? suggestion.name ?? '';

export const identifierTypeForValue = (
  identifier: District['name' | 'number' | 'letter'],
): DistrictIdentifierType => {
  const trimmed = `${identifier ?? ''}`.trim();
  if (typeof identifier === 'number' || /^\d+$/.test(trimmed)) return 'number';
  return trimmed.length === 1 ? 'letter' : 'name';
};

export const tierForDistrictType = (
  type: string | undefined,
  stateConfiguration?: StateConfiguration | null,
): number | undefined =>
  stateConfiguration?.typeConfigurations.find(configuration => configuration.type === type)?.tierNumber;

export const siblingDistrictQuery = (
  suggestion: CreateDistrictSuggestion,
  stateConfiguration?: StateConfiguration | null,
) => {
  if (
    tierForDistrictType(suggestion.type, stateConfiguration) !== 3
    || !suggestion.parentId
    || !suggestion.type
  ) {
    return null;
  }

  return {
    parentId: suggestion.parentId,
    type: suggestion.type,
    $sort: { name: 1 },
  };
};

export const districtRowsForSuggestion = (
  siblings: District[],
  suggestion: CreateDistrictSuggestion,
): DistrictRow[] => {
  const identifierType = identifierTypeForSuggestion(suggestion);
  const existingRows = siblings.map((district): DistrictRow => {
    const identifier = district[identifierType] ?? identifierValueForDistrict(district);
    return {
      identifier,
      originalIdentifier: identifier,
      longName: district.longName,
      votingType: district.votingType,
      existingDistrictId: district._id,
    };
  });
  const suggestionIdentifier = identifierValueForSuggestion(suggestion);

  return [
    ...existingRows,
    {
      identifier: suggestionIdentifier,
      originalIdentifier: suggestionIdentifier,
      longName: suggestion.longName,
      votingType: 'voting-district',
    },
  ];
};

export const orderNamingPatterns = (
  parentPattern?: NamingPattern | null,
  nationalPattern?: NamingPattern | null,
): NamingPattern[] => {
  if (!parentPattern) return nationalPattern ? [nationalPattern] : [];
  if (!nationalPattern || nationalPattern._id === parentPattern._id) return [parentPattern];
  return [parentPattern, nationalPattern];
};

export const hasNamingPatternForIdentifier = (
  districtType: string | undefined,
  identifierType: DistrictIdentifierType,
  namingPatterns: NamingPattern[],
): boolean => {
  if (!districtType) return false;
  const segmentPrefix = `[IF {{${identifierType.toUpperCase()}}}:`;

  return namingPatterns.some(pattern => {
    const configuration = pattern.configuration?.[districtType];
    return Boolean(
      configuration?.long?.en?.includes(segmentPrefix)
      && configuration?.short?.en?.includes(segmentPrefix),
    );
  });
};

export const districtNamePreview = (
  row: Pick<DistrictRow, 'identifier'>,
  identifierType: DistrictIdentifierType,
  suggestion: Pick<CreateDistrictSuggestion, 'type' | 'parentDistrict'>,
  namingPatterns: NamingPattern[],
): string => {
  if (!hasNamingPatternForIdentifier(suggestion.type, identifierType, namingPatterns)) return '';

  return prettyDistrictNameFromPattern({
    ...identifierPayload(identifierType, row.identifier),
    type: suggestion.type,
    parent: suggestion.parentDistrict,
  }, namingPatterns).longName;
};

export const childrenStructureForCreation = (
  parentDistrict: Pick<District, 'childrenStructure'>,
  childType: string,
  childCount: number,
) => ({
  ...(parentDistrict.childrenStructure || {}),
  [childType]: childCount,
  validatedAt: null,
});

export const parentMetadataForCreation = (
  parentDistrict: Pick<District, 'notes' | 'sources'>,
  childType: string,
  creationSources: DistrictSource[] = [],
  creationNotes: string[] = [],
): Pick<District, 'notes' | 'sources'> => {
  const sources = [
    ...creationSources.map(source => ({ ...source, type: childType })),
    ...(parentDistrict.sources || []),
  ].filter((source, index, allSources) => (
    allSources.findIndex(candidate => (
      candidate.type === source.type && candidate.url === source.url
    )) === index
  ));
  const prefixedNotes = creationNotes
    .map(note => note.trim())
    .filter(Boolean)
    .map(note => `${childType}:${note}`);
  const notes = prefixedNotes.length > 0
    ? [
      ...(parentDistrict.notes || []).filter(note => !note.startsWith(`${childType}:`)),
      ...prefixedNotes,
    ]
    : [...(parentDistrict.notes || [])];

  return { notes, sources };
};

type Position = number[];
type Ring = Position[];
type PolygonCoordinates = Ring[];
type MultiPolygonCoordinates = PolygonCoordinates[];

export interface AtLargeSuggestion {
  suggestedAtLarge: boolean;
  message: string;
  childAreaPercentage?: number;
}

const isPosition = (value: unknown): value is Position =>
  Array.isArray(value)
  && value.length >= 2
  && typeof value[0] === 'number'
  && typeof value[1] === 'number';

const isRing = (value: unknown): value is Ring =>
  Array.isArray(value) && value.length >= 3 && value.every(isPosition);

const isPolygonCoordinates = (value: unknown): value is PolygonCoordinates =>
  Array.isArray(value) && value.length > 0 && value.every(isRing);

const isMultiPolygonCoordinates = (value: unknown): value is MultiPolygonCoordinates =>
  Array.isArray(value) && value.length > 0 && value.every(isPolygonCoordinates);

const geometryDetails = (
  value: unknown,
): { type?: string; coordinates: unknown } | null => {
  if (Array.isArray(value)) return { coordinates: value };
  if (!value || typeof value !== 'object') return null;

  const candidate = value as {
    type?: string;
    coordinates?: unknown;
    geometry?: unknown;
  };
  if (candidate.type === 'Feature') return geometryDetails(candidate.geometry);
  if (candidate.coordinates !== undefined) {
    return { type: candidate.type, coordinates: candidate.coordinates };
  }
  return null;
};

const radians = (degrees: number) => degrees * Math.PI / 180;
const earthRadiusMeters = 6378137;

const ringArea = (ring: Ring): number => {
  let total = 0;
  for (let index = 0; index < ring.length; index += 1) {
    const lower = ring[(index + ring.length - 1) % ring.length];
    const middle = ring[index];
    const upper = ring[(index + 1) % ring.length];
    total += (radians(upper[0]) - radians(lower[0])) * Math.sin(radians(middle[1]));
  }
  return Math.abs(total * earthRadiusMeters * earthRadiusMeters / 2);
};

const polygonArea = (polygon: PolygonCoordinates): number => {
  const [outerRing, ...holes] = polygon;
  return Math.max(0, ringArea(outerRing) - holes.reduce((total, hole) => total + ringArea(hole), 0));
};

export const geometryArea = (value: unknown): number | null => {
  const geometry = geometryDetails(value);
  if (!geometry) return null;

  if (geometry.type === 'Polygon' || (!geometry.type && isPolygonCoordinates(geometry.coordinates))) {
    return isPolygonCoordinates(geometry.coordinates)
      ? polygonArea(geometry.coordinates)
      : null;
  }
  if (
    geometry.type === 'MultiPolygon'
    || (!geometry.type && isMultiPolygonCoordinates(geometry.coordinates))
  ) {
    return isMultiPolygonCoordinates(geometry.coordinates)
      ? geometry.coordinates.reduce((total, polygon) => total + polygonArea(polygon), 0)
      : null;
  }
  return null;
};

export const suggestAtLargeStatus = (
  parentHasGeodistrict: boolean,
  parentGeometry?: unknown,
  childGeometry?: unknown,
): AtLargeSuggestion => {
  if (!parentHasGeodistrict) {
    return {
      suggestedAtLarge: false,
      message: 'No at-large suggestion: the parent district does not have a geodistrict.',
    };
  }
  if (!childGeometry) {
    return {
      suggestedAtLarge: false,
      message: 'No at-large suggestion: geometry is unavailable for the suggested district.',
    };
  }

  const parentArea = geometryArea(parentGeometry);
  const childArea = geometryArea(childGeometry);
  if (!parentArea || !childArea) {
    return {
      suggestedAtLarge: false,
      message: 'No at-large suggestion: the parent or suggested geometry could not be measured.',
    };
  }

  const childAreaPercentage = childArea / parentArea * 100;
  const suggestedAtLarge = childAreaPercentage >= 90;
  return {
    suggestedAtLarge,
    childAreaPercentage,
    message: `New boundary is ${childAreaPercentage.toFixed(1)}% of parent area.`,
  };
};
